import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

import '@repo/ui/CSSBase/root.css';
import '@repo/ui/CSSBase/font-figtree.css';
import { ErrorBoundaryFallback } from '@repo/ui/ErrorBoundaryFallback';
import { ToastRoot } from '@repo/ui/Toast/ToastRoot';
import { rawString } from './components/Language/Language';
import { LanguageProviderWrapper } from './components/Language/LanguageProviderWrapper';
import { queryClient } from './queryClient';
import { createAppRouter, errorBoundaryTitle } from './router';
import { clientLogger, syncRollbarContextFromUrl } from './utils/logger';

const router = createAppRouter();

if (typeof window !== 'undefined') {
  syncRollbarContextFromUrl();

  window.addEventListener('error', (e: ErrorEvent) => {
    const diagnostics = {
      errorEventFilename: e.filename || undefined,
      errorEventLineno: e.lineno,
      errorEventColno: e.colno,
      errorEventMessage: e.message,
    };
    if (e.error) {
      clientLogger.logError(e.error, 'window error', diagnostics);
    } else {
      clientLogger.logError(
        new Error(e.message || 'Unknown window error'),
        'window error (no Error object)',
        diagnostics,
      );
    }
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    clientLogger.logError(e.reason, 'unhandled promise rejection', {
      promiseRejection: true,
    });
  });

  const globalSubscriptions = globalThis as typeof globalThis & {
    __pdpOnResolvedUnsubscribe__?: () => void;
  };
  globalSubscriptions.__pdpOnResolvedUnsubscribe__?.();
  globalSubscriptions.__pdpOnResolvedUnsubscribe__ = router.subscribe(
    'onResolved',
    (event) => {
      clientLogger.recordRouteChange(event.toLocation.pathname);
    },
  );
}

// Register things for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

declare module '@tanstack/history' {
  interface HistoryState {
    /**
     * Set when the previous-requests list opens a claim, so the claim's Back can return to that
     * list through history. Any other arrival — a direct link, or the redirects that put a
     * retailer into the path — leaves an entry behind that is not a page to go back to.
     */
    claimOpenedFromRequests?: true;
  }
}

const rootElement = document.getElementById('app')!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <ErrorBoundary
      fallbackRender={({ error }) => {
        const { correlationId } = clientLogger.logError(error);
        const message = error instanceof Error ? error.message : String(error);
        return (
          <ErrorBoundaryFallback
            message={message}
            title={errorBoundaryTitle}
            metaInfo={rawString({
              langKey: 'pdx.global.correlation_id',
              replacementTokens: { correlation_id: correlationId ?? '' },
            })}
          />
        );
      }}
    >
      <QueryClientProvider client={queryClient}>
        <LanguageProviderWrapper>
          <ToastRoot />
          <RouterProvider router={router} />
        </LanguageProviderWrapper>
      </QueryClientProvider>
    </ErrorBoundary>,
  );
}
